import type { Menu, Product } from "@/redux/services/menu/menu.type";
import { getMenuItemDisplayPrice } from "@/lib/utils";

/**
 * Lowest displayed price of each dish across its placements, in cents, keyed
 * by product id. Each placement is priced with `getMenuItemDisplayPrice`, the
 * helper the menu cards use, so a dish priced only through a size option
 * ("Taille": Petite/Moyenne/Grande) gets its smallest size (SFRESTO-146).
 *
 * The products endpoint carries no options, so the catalogue reads them from
 * the menus (GET /menus embeds placements with their dish and options).
 */
export function buildProductFromPrices(
  menus: Menu[] | undefined
): Map<string, number> {
  const prices = new Map<string, number>();
  for (const menu of menus ?? []) {
    for (const section of menu.menuSections ?? []) {
      for (const item of section.menuItems ?? []) {
        const productId = item.product?.id;
        if (!productId) continue;
        const price = getMenuItemDisplayPrice(item);
        if (price <= 0) continue;
        const current = prices.get(productId);
        if (current === undefined || price < current) {
          prices.set(productId, price);
        }
      }
    }
  }
  return prices;
}

export interface ProductDisplayPrice {
  /** Cents. */
  amount: number;
  /** True when the amount is the smallest option price ("À partir de"). */
  isFrom: boolean;
}

/** The dish's own price, else its "from" price, else 0. */
export function getProductDisplayPrice(
  product: Pick<Product, "basePrice">,
  fromPrice: number | undefined
): ProductDisplayPrice {
  if (product.basePrice > 0) {
    return { amount: product.basePrice, isFrom: false };
  }
  const from = fromPrice ?? 0;
  return from > 0
    ? { amount: from, isFrom: true }
    : { amount: 0, isFrom: false };
}

/**
 * Whether any listed dish needs a "from" price: it has no own price and is on
 * at least one menu (a dish on no menu has no placement to read options from).
 * Lets the catalogue skip loading the menus in the common case.
 */
export function needsProductFromPrice(
  products: Pick<Product, "basePrice" | "menuNames">[]
): boolean {
  return products.some(
    (product) => product.basePrice <= 0 && (product.menuNames?.length ?? 0) > 0
  );
}
